/**
 * App Lifecycle Service
 * 
 * Servicio para gestionar el ciclo de vida de la app nativa.
 * Maneja eventos de foreground/background, deep links, etc.
 * REQUERIDO para comportamiento nativo correcto.
 * 
 * @module native/app-lifecycle
 */

import { App, URLOpenListenerEvent, AppState } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('AppLifecycleService');

export interface AppInfo {
  name: string;
  id: string;
  build: string;
  version: string;
}

type AppStateChangeCallback = (isActive: boolean) => void;
type DeepLinkCallback = (url: string) => void;

class AppLifecycleService {
  private static instance: AppLifecycleService;
  private isInitialized = false;
  private stateListeners: Set<AppStateChangeCallback> = new Set();
  private deepLinkListeners: Set<DeepLinkCallback> = new Set();
  private currentState: 'active' | 'background' = 'active';

  private constructor() {}

  static getInstance(): AppLifecycleService {
    if (!AppLifecycleService.instance) {
      AppLifecycleService.instance = new AppLifecycleService();
    }
    return AppLifecycleService.instance;
  }

  /**
   * Verifica si estamos en un dispositivo nativo
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Verifica si estamos en iOS
   */
  isIOS(): boolean {
    return Capacitor.getPlatform() === 'ios';
  }

  /**
   * Verifica si estamos en Android
   */
  isAndroid(): boolean {
    return Capacitor.getPlatform() === 'android';
  }

  /**
   * Inicializa el servicio de ciclo de vida
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || !this.isNativePlatform()) {
      return;
    }

    try {
      // Configurar Status Bar
      await this.configureStatusBar();

      // Ocultar splash screen
      await this.hideSplashScreen();

      // Configurar listeners de estado
      this.setupAppStateListeners();

      // Configurar deep links
      this.setupDeepLinkListeners();

      // Configurar teclado
      this.setupKeyboardListeners();

      this.isInitialized = true;
      logger.info('AppLifecycleService inicializado');
    } catch (error) {
      logger.error('Error inicializando AppLifecycleService', { error });
    }
  }

  /**
   * Configura la barra de estado
   */
  private async configureStatusBar(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      // Estilo por defecto: contenido oscuro sobre fondo claro
      await StatusBar.setStyle({ style: Style.Light });
      
      // En iOS, hacemos la barra de estado overlay
      if (this.isIOS()) {
        await StatusBar.setOverlaysWebView({ overlay: false });
      }

      // Mostrar la barra de estado
      await StatusBar.show();

      logger.info('Status bar configurada');
    } catch (error) {
      logger.error('Error configurando status bar', { error });
    }
  }

  /**
   * Cambia el estilo de la barra de estado
   */
  async setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await StatusBar.setStyle({ 
        style: style === 'dark' ? Style.Dark : Style.Light 
      });
    } catch (error) {
      logger.error('Error cambiando estilo de status bar', { error });
    }
  }

  /**
   * Oculta el splash screen
   */
  async hideSplashScreen(fadeOutDuration: number = 300): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await SplashScreen.hide({
        fadeOutDuration,
      });
      logger.info('Splash screen ocultado');
    } catch (error) {
      logger.error('Error ocultando splash screen', { error });
    }
  }

  /**
   * Muestra el splash screen
   */
  async showSplashScreen(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await SplashScreen.show({
        autoHide: false,
      });
    } catch (error) {
      logger.error('Error mostrando splash screen', { error });
    }
  }

  /**
   * Configura listeners de estado de la app
   */
  private setupAppStateListeners(): void {
    App.addListener('appStateChange', (state: AppState) => {
      const isActive = state.isActive;
      this.currentState = isActive ? 'active' : 'background';
      
      logger.info('Cambio de estado de app', { isActive });
      
      // Notificar a todos los listeners
      this.stateListeners.forEach(callback => {
        try {
          callback(isActive);
        } catch (error) {
          logger.error('Error en listener de estado', { error });
        }
      });
    });

    // Listener para cuando la app va a cerrarse
    App.addListener('backButton', () => {
      logger.info('Back button presionado');
      // En Android, podemos manejar el botón de atrás
      // Por defecto, dejar que la navegación del router maneje esto
    });
  }

  /**
   * Configura listeners de deep links
   */
  private setupDeepLinkListeners(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      logger.info('Deep link recibido', { url: event.url });
      
      // Notificar a todos los listeners
      this.deepLinkListeners.forEach(callback => {
        try {
          callback(event.url);
        } catch (error) {
          logger.error('Error en listener de deep link', { error });
        }
      });
    });
  }

  /**
   * Configura listeners del teclado
   */
  private setupKeyboardListeners(): void {
    if (!this.isNativePlatform()) return;

    Keyboard.addListener('keyboardWillShow', (info) => {
      logger.debug('Teclado aparecerá', { height: info.keyboardHeight });
      document.body.classList.add('keyboard-open');
      document.documentElement.style.setProperty(
        '--keyboard-height', 
        `${info.keyboardHeight}px`
      );
    });

    Keyboard.addListener('keyboardWillHide', () => {
      logger.debug('Teclado se ocultará');
      document.body.classList.remove('keyboard-open');
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    });
  }

  /**
   * Obtiene información de la app
   */
  async getAppInfo(): Promise<AppInfo | null> {
    if (!this.isNativePlatform()) return null;

    try {
      const info = await App.getInfo();
      return {
        name: info.name,
        id: info.id,
        build: info.build,
        version: info.version,
      };
    } catch (error) {
      logger.error('Error obteniendo info de app', { error });
      return null;
    }
  }

  /**
   * Verifica si la app está activa
   */
  isAppActive(): boolean {
    return this.currentState === 'active';
  }

  /**
   * Registra un listener para cambios de estado
   */
  onStateChange(callback: AppStateChangeCallback): () => void {
    this.stateListeners.add(callback);
    return () => this.stateListeners.delete(callback);
  }

  /**
   * Registra un listener para deep links
   */
  onDeepLink(callback: DeepLinkCallback): () => void {
    this.deepLinkListeners.add(callback);
    return () => this.deepLinkListeners.delete(callback);
  }

  /**
   * Cierra la app (solo Android)
   */
  async exitApp(): Promise<void> {
    if (this.isAndroid()) {
      await App.exitApp();
    }
  }

  /**
   * Oculta el teclado
   */
  async hideKeyboard(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await Keyboard.hide();
    } catch (error) {
      logger.error('Error ocultando teclado', { error });
    }
  }

  /**
   * Muestra el teclado (enfoca elemento)
   */
  async showKeyboard(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await Keyboard.show();
    } catch (error) {
      logger.error('Error mostrando teclado', { error });
    }
  }

  /**
   * Limpieza al destruir
   */
  async cleanup(): Promise<void> {
    if (!this.isNativePlatform()) return;

    try {
      await App.removeAllListeners();
      await Keyboard.removeAllListeners();
      this.stateListeners.clear();
      this.deepLinkListeners.clear();
      this.isInitialized = false;
      logger.info('AppLifecycleService cleanup completado');
    } catch (error) {
      logger.error('Error en cleanup de AppLifecycleService', { error });
    }
  }
}

export const appLifecycleService = AppLifecycleService.getInstance();










